import React, { useEffect, useMemo, useRef, useState } from "react";
import { decodeItalianFiscalCode } from "./Rpf26SmartInput";

// Generated from the original PF2026 Engine styles, preserving their order and
// declarations while namespacing selectors to this embedded workspace only.
const STYLES = ["embedded.css"];
const FRONTESPIZIO_ASSET_VERSION = "20260909-3";
const LM_ASSET_VERSION = "20260907-3";

const FRONT_REQUIRED_CONTROL_IDS = [
  "fiscalCode", "lastName", "firstName", "birthProvince", "birthPlace", "birthDate", "sex",
  "domicile-2025-municipality", "domicile-2025-province", "domicile-2025-cadastral-code",
  "eight-recipient", "five-purpose", "two-party",
];

export const RPF26_FRONT_REQUIRED = [
  ["frontespizio.codice_fiscale", "fiscalCode", "Campo Codice fiscale non compilato"],
  ["frontespizio.cognome", "lastName", "Campo Cognome non compilato"],
  ["frontespizio.nome", "firstName", "Campo Nome non compilato"],
  ["frontespizio.provincia_nascita", "birthProvince", "Campo Provincia di nascita non compilato"],
  ["frontespizio.comune_nascita", "birthPlace", "Campo Comune o Stato estero di nascita non compilato"],
  ["frontespizio.data_nascita", "birthDate", "Campo Data di nascita non compilato"],
  ["frontespizio.domicilio_fiscale_comune", "domicile-2025-municipality", "Campo Comune del domicilio fiscale al 01/01/2025 non compilato"],
  ["frontespizio.domicilio_fiscale_provincia", "domicile-2025-province", "Campo Provincia del domicilio fiscale al 01/01/2025 non compilato"],
  ["frontespizio.domicilio_fiscale_codice_comune", "domicile-2025-cadastral-code", "Campo Codice comune catastale del domicilio fiscale al 01/01/2025 non compilato"],
  ["frontespizio.sesso", "sex", "Campo Sesso non compilato"],
];

export function validateRpf26Frontespizio(form, telematicProfile = {}) {
  const errors = RPF26_FRONT_REQUIRED.flatMap(([field, controlId, message]) =>
    String(getPath(form, field) ?? "").trim() ? [] : [{ field, controlId, message }]);
  const front = form?.frontespizio || {};
  if (telematicProfile.isIntermediary === true) {
    [
      ["impegno_codice_fiscale_incaricato", "commitment-intermediary-tax-code", "Codice fiscale dell'incaricato non compilato"],
      ["impegno_soggetto_predisponente", "commitment-prepared-by", "Soggetto che ha predisposto la dichiarazione non selezionato"],
      ["impegno_data", "commitment-date", "Data dell'impegno non compilata"],
    ].forEach(([key, controlId, message]) => {
      if (!String(front[key] ?? "").trim()) errors.push({ field: `frontespizio.${key}`, controlId, message });
    });
    if (front.impegno_firma_incaricato !== true) errors.push({ field: "frontespizio.impegno_firma_incaricato", controlId: "commitment-signature", message: "Firma dell'incaricato non confermata" });
    if (front.impegno_invio_avviso_controllo_intermediario === true && front.impegno_ricezione_avviso_controllo !== true) {
      errors.push({ field: "frontespizio.impegno_ricezione_avviso_controllo", controlId: "commitment-receive-control", message: "L’intermediario deve accettare la ricezione dell’avviso telematico richiesto dal contribuente" });
    }
    if (front.impegno_invio_altre_comunicazioni_intermediario === true && front.impegno_ricezione_altre_comunicazioni !== true) {
      errors.push({ field: "frontespizio.impegno_ricezione_altre_comunicazioni", controlId: "commitment-receive-other", message: "L’intermediario deve accettare la ricezione delle altre comunicazioni richieste dal contribuente" });
    }
  }
  [
    ["scelta_otto_per_mille", "scelta_otto_per_mille_non_effettuata", "eight-recipient", "Scelta dell’otto per mille non effettuata"],
    ["scelta_cinque_per_mille", "scelta_cinque_per_mille_non_effettuata", "five-purpose", "Scelta del cinque per mille non effettuata"],
    ["scelta_due_per_mille_partito", "scelta_due_per_mille_non_effettuata", "two-party", "Scelta del due per mille non effettuata"],
  ].forEach(([choice, noChoice, controlId, message]) => {
    if (!String(front[choice] ?? "").trim() && front[noChoice] !== true) errors.push({ field: `frontespizio.${choice}`, controlId, message });
  });
  if (front.scelta_otto_per_mille === "STATO" && !String(front.scelta_otto_stato_finalita ?? "").trim()) {
    errors.push({ field: "frontespizio.scelta_otto_stato_finalita", controlId: "eight-state-purpose", message: "Finalità della scelta dello Stato per l’otto per mille non selezionata" });
  }
  if (!errors.some((error) => ["frontespizio.codice_fiscale", "frontespizio.cognome", "frontespizio.nome"].includes(error.field))
      && !isNameConsistentWithFiscalCode(front.cognome, front.nome, front.codice_fiscale)) {
    const message = "Incongruenza tra il Cognome o nome e il Codice Fiscale.";
    errors.push({ field: "frontespizio.cognome", controlId: "lastName", message });
    errors.push({ field: "frontespizio.nome", controlId: "firstName", message });
  }
  return errors;
}

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

const normalizeName = (value) => String(value || "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z]/g, "");

function surnameCode(value) {
  const text = normalizeName(value); const consonants = text.replace(/[AEIOU]/g, ""); const vowels = text.replace(/[^AEIOU]/g, "");
  return `${consonants}${vowels}XXX`.slice(0, 3);
}

function firstNameCode(value) {
  const text = normalizeName(value); const consonants = text.replace(/[AEIOU]/g, ""); const vowels = text.replace(/[^AEIOU]/g, "");
  if (consonants.length >= 4) return `${consonants[0]}${consonants[2]}${consonants[3]}`;
  return `${consonants}${vowels}XXX`.slice(0, 3);
}

export function isNameConsistentWithFiscalCode(lastName, firstName, fiscalCode) {
  const cf = String(fiscalCode || "").trim().toUpperCase();
  if (!/^[A-Z0-9]{16}$/.test(cf) || !normalizeName(lastName) || !normalizeName(firstName)) return true;
  return cf.slice(0, 3) === surnameCode(lastName) && cf.slice(3, 6) === firstNameCode(firstName);
}

export function validateRpf26LmRs(form) {
  const lm = form?.quadri?.LM || {};
  const lm21 = lm.LM21 || {};
  const activities = Array.isArray(lm.attivita) ? lm.attivita : [22,23,24,25,26,27].map((row) => lm[`LM${row}`]).filter(Boolean);
  const lmUsed = Object.values(lm21).some(hasValue) || activities.some((row) => row && Object.values(row).some(hasValue));
  if (!lmUsed) return [];
  const errors = [];
  if (!["IMPRESA", "AUTONOMO", "IMPRESA_FAMILIARE"].includes(lm.tipologia_attivita)) errors.push({ field: "quadri.LM.tipologia_attivita", selector: 'input[name="qualification"]', message: "Qualifica attività obbligatoria" });
  if (!hasValue(lm21.ateco_2025)) errors.push({ field: "quadri.LM.LM21.ateco_2025", controlId: "ateco2025", message: "LM21 colonna 4: codice ATECO 2025 obbligatorio" });
  if (lm21.requisiti_accesso_e_assenza_cause_cessazione !== true) errors.push({ field: "quadri.LM.LM21.requisiti_accesso_e_assenza_cause_cessazione", controlId: "requirements", message: "LM21 colonna 1: requisiti di accesso non verificati" });
  if (lm21.assenza_cause_ostative !== true) errors.push({ field: "quadri.LM.LM21.assenza_cause_ostative", controlId: "noExclusions", message: "LM21 colonna 2: assenza di cause ostative non verificata" });
  const populated = activities.map((row, index) => ({ row: row || {}, index })).filter(({ row }) => Object.values(row).some(hasValue));
  if (!populated.length) errors.push({ field: "quadri.LM.attivita.0.codice_attivita", selector: '#activity-rows tr[data-row="22"] [data-field="ateco"]', message: "LM22: indicare almeno un’attività" });
  populated.forEach(({ row, index }) => {
    const number = 22 + index;
    if (!hasValue(row.codice_attivita)) errors.push({ field: `quadri.LM.attivita.${index}.codice_attivita`, selector: `#activity-rows tr[data-row="${number}"] [data-field="ateco"]`, message: `LM${number}: codice ATECO 2007 obbligatorio` });
    if (![row.componenti_positivi, row.diritti_autore].some((value) => hasValue(value) && Number(value) !== 0)) errors.push({ field: `quadri.LM.attivita.${index}.componenti_positivi`, selector: `#activity-rows tr[data-row="${number}"] [data-field="positive"]`, message: `LM${number}: indicare componenti positivi oppure diritti d’autore` });
  });
  const rs = form?.quadri?.RS || {};
  const rsInfo = ["RS375001","RS376001","RS377001","RS378001","RS381001"].some((key) => hasValue(rs[key]) && Number(rs[key]) !== 0);
  const rs382 = rs.RS382000 === true || rs.rs382 === true;
  if (!rsInfo && !rs382) errors.push({ field: "quadri.RS.RS382000", selector: "#rs375,#rs376,#rs377,#rs378,#rs381,#rs382", targetView: "RS", message: "Codice LM 021 001 - In presenza di un dato della sezione III del quadro LM deve essere presente almeno un elemento conoscitivo dei righi da RS375 a RS381; in assenza di dati da comunicare all'Anagrafe Tributaria, deve essere invece barrata l'apposita casella RS382" });
  return errors;
}

const FRONT_PATHS = {
  "declaration-730-extra": "frontespizio.quadri_aggiuntivi_730", "declaration-cpb": "frontespizio.comunicazione_cpb",
  "declaration-corrective": "frontespizio.correttiva_termini", "declaration-supplementary": "frontespizio.dichiarazione_integrativa",
  "declaration-supplementary-8ter": "frontespizio.integrativa_8ter", "declaration-accounting-errors": "frontespizio.integrativa_errori_contabili",
  "declaration-exceptional-events": "frontespizio.eventi_eccezionali", "declaration-730-correction": "frontespizio.rettificativa_730",
  fiscalCode: "frontespizio.codice_fiscale", lastName: "frontespizio.cognome", firstName: "frontespizio.nome",
  birthProvince: "frontespizio.provincia_nascita", birthPlace: "frontespizio.comune_nascita", birthDate: "frontespizio.data_nascita",
  foreignStateCode: "frontespizio.codice_stato_estero", vatNumber: "frontespizio.partita_iva",
  "dormant-inheritance": "frontespizio.accettazione_eredita_giacente", "voluntary-liquidation": "frontespizio.liquidazione_volontaria",
  "seized-properties": "frontespizio.immobili_sequestrati", "business-cessation": "frontespizio.cessazione_attivita",
  "liquidation-art18": "frontespizio.liquidazione_art18", "liquidation-closing-date": "frontespizio.liquidazione_data_chiusura",
  "liquidator-status": "frontespizio.liquidatore_stato", "tax-period-from": "frontespizio.periodo_imposta_dal", "tax-period-to": "frontespizio.periodo_imposta_al",
  "residence-municipality": "frontespizio.residenza_comune", "residence-province": "frontespizio.residenza_provincia",
  "residence-postcode": "frontespizio.residenza_cap", "residence-cadastral-code": "frontespizio.residenza_codice_comune",
  "residence-address-type": "frontespizio.residenza_tipologia", "residence-address": "frontespizio.residenza_indirizzo",
  "residence-house-number": "frontespizio.residenza_civico", "residence-hamlet": "frontespizio.residenza_frazione",
  "residence-change-date": "frontespizio.residenza_data_variazione", "residence-from-other-municipality": "frontespizio.residenza_variazione_altro_comune",
  "residence-different-domicile": "frontespizio.domicilio_diverso_residenza", "first-declaration": "frontespizio.prima_dichiarazione",
  "phone-prefix": "frontespizio.telefono_prefisso", "phone-number": "frontespizio.telefono", "mobile-number": "frontespizio.cellulare", "email-address": "frontespizio.email",
  "domicile-2025-municipality": "frontespizio.domicilio_fiscale_comune", "domicile-2025-province": "frontespizio.domicilio_fiscale_provincia",
  "domicile-2025-cadastral-code": "frontespizio.domicilio_fiscale_codice_comune", "domicile-2026-municipality": "frontespizio.domicilio_fiscale_2026_comune",
  "domicile-2026-province": "frontespizio.domicilio_fiscale_2026_provincia", "domicile-2026-cadastral-code": "frontespizio.domicilio_fiscale_2026_codice_comune",
  "declaration-signature": "frontespizio.firma_dichiarante",
  deceased: "frontespizio.situazione_contribuente", represented: "frontespizio.situazione_contribuente", minor: "frontespizio.situazione_contribuente",
  "foreign-fiscal-code": "frontespizio.estero_codice_fiscale", "foreign-residence-state": "frontespizio.estero_stato",
  "foreign-residence-code": "frontespizio.estero_codice_stato", "foreign-region": "frontespizio.estero_regione",
  "foreign-locality": "frontespizio.estero_localita", "foreign-address": "frontespizio.estero_indirizzo",
  schumacker: "frontespizio.estero_schumacker", "foreign-nationality": "frontespizio.nazionalita_estera",
  "italian-nationality": "frontespizio.nazionalita_italiana", "representative-fiscal-code": "frontespizio.rappresentante_codice_fiscale",
  "representative-role": "frontespizio.rappresentante_codice_carica", "representative-role-date": "frontespizio.rappresentante_data_carica",
  "representative-last-name": "frontespizio.rappresentante_cognome", "representative-first-name": "frontespizio.rappresentante_nome",
  "representative-birth-date": "frontespizio.rappresentante_data_nascita", "representative-birth-place": "frontespizio.rappresentante_comune_nascita",
  "representative-birth-state": "frontespizio.rappresentante_comune_nascita", "representative-birth-province": "frontespizio.rappresentante_provincia_nascita",
  "representative-residence-municipality": "frontespizio.rappresentante_comune_residenza", "representative-residence-state": "frontespizio.rappresentante_comune_residenza",
  "representative-residence-province": "frontespizio.rappresentante_provincia_residenza", "representative-residence-cap": "frontespizio.rappresentante_cap",
  "representative-address": "frontespizio.rappresentante_indirizzo", "representative-phone": "frontespizio.rappresentante_telefono",
  "procedure-start": "frontespizio.procedura_data_inizio", "procedure-open": "frontespizio.procedura_non_terminata",
  "procedure-end": "frontespizio.procedura_data_fine", "representative-abroad": "frontespizio.rappresentante_residente_estero",
  "declaring-entity-fiscal-code": "frontespizio.ente_dichiarante_codice_fiscale", "rai-business": "frontespizio.canone_rai_imprese",
  "commitment-intermediary-tax-code": "frontespizio.impegno_codice_fiscale_incaricato",
  "commitment-prepared-by": "frontespizio.impegno_soggetto_predisponente",
  "commitment-send-control": "frontespizio.impegno_invio_avviso_controllo_intermediario",
  "commitment-send-other": "frontespizio.impegno_invio_altre_comunicazioni_intermediario",
  "commitment-receive-control": "frontespizio.impegno_ricezione_avviso_controllo",
  "commitment-receive-other": "frontespizio.impegno_ricezione_altre_comunicazioni",
  "commitment-date": "frontespizio.impegno_data",
  "commitment-signature": "frontespizio.impegno_firma_incaricato",
  "visa-caf-manager-tax-code": "frontespizio.visto_cf_responsabile_caf",
  "visa-caf-tax-code": "frontespizio.visto_cf_caf",
  "visa-professional-tax-code": "frontespizio.visto_cf_professionista",
  "visa-signature": "frontespizio.visto_firma",
  "visa-superbonus": "frontespizio.visto_superbonus",
  "certification-professional-tax-code": "frontespizio.certificazione_cf_professionista",
  "certification-other-tax-code": "frontespizio.certificazione_cf_soggetto_diverso",
  "certification-signature": "frontespizio.certificazione_firma",
  "eight-recipient": "frontespizio.scelta_otto_per_mille", "eight-state-purpose": "frontespizio.scelta_otto_stato_finalita",
  "eight-no-choice": "frontespizio.scelta_otto_per_mille_non_effettuata", "five-purpose": "frontespizio.scelta_cinque_per_mille",
  "five-tax-code": "frontespizio.scelta_cinque_codice_fiscale", "five-no-choice": "frontespizio.scelta_cinque_per_mille_non_effettuata",
  "two-party": "frontespizio.scelta_due_per_mille_partito", "two-no-choice": "frontespizio.scelta_due_per_mille_non_effettuata",
  "all-no-choice": "frontespizio.scelte_per_mille_non_effettuate",
};

const LM_PATHS = {
  requirements: "quadri.LM.LM21.requisiti_accesso_e_assenza_cause_cessazione", noExclusions: "quadri.LM.LM21.assenza_cause_ostative",
  newActivity: "quadri.LM.LM21.nuova_attivita", ateco2025: "quadri.LM.LM21.ateco_2025",
  priorYearGrossReceipts: "quadri.LM.questionario_forfettario.ricavi_compensi_anno_precedente",
  priorYearEmploymentCosts: "quadri.LM.questionario_forfettario.spese_lavoro_anno_precedente",
  priorYearEmploymentIncome: "quadri.LM.questionario_forfettario.redditi_lavoro_anno_precedente",
  residencyEligible: "quadri.LM.questionario_forfettario.residenza_ammessa",
  usesSpecialVatOrIncomeRegime: "quadri.LM.questionario_forfettario.regime_speciale_incompatibile",
  primarilyRealEstateLandOrNewVehicles: "quadri.LM.questionario_forfettario.attivita_immobili_terreni_mezzi_nuovi",
  primarilyRealEstateLandOrNewVehicles: "quadri.LM.questionario_forfettario.attivita_immobili_terreni_mezzi_nuovi",
  hasDisallowedParticipation: "quadri.LM.questionario_forfettario.partecipazione_controllo_incompatibile",
  mainlyWorksForCurrentOrRecentEmployer: "quadri.LM.questionario_forfettario.prevalenza_datore_attuale_o_precedenti",
  employmentRelationshipEnded: "quadri.LM.questionario_forfettario.rapporto_lavoro_cessato",
  startYear: "quadri.LM.questionario_forfettario.nuova_attivita.anno_inizio",
  newSimilarPrevious: "quadri.LM.questionario_forfettario.nuova_attivita.nessuna_attivita_simile_tre_anni",
  newContinuation: "quadri.LM.questionario_forfettario.nuova_attivita.non_prosecuzione",
  newPracticeException: "quadri.LM.questionario_forfettario.nuova_attivita.eccezione_praticantato",
  newTakenOver: "quadri.LM.questionario_forfettario.nuova_attivita.attivita_proseguita",
  takenOverPriorYearGrossReceipts: "quadri.LM.questionario_forfettario.nuova_attivita.ricavi_precedente_titolare",
  lm34_1_input: "quadri.LM.LM34.reddito_lordo_artigiani_commercianti", lm34_2_input: "quadri.LM.LM34.reddito_lordo_gestione_separata",
  lm34_3: "quadri.LM.LM34.reddito_lordo", contributions: "quadri.LM.LM35.contributi_previdenziali",
  lm35_2: "quadri.LM.LM35.contributi_deducibili", lm36: "quadri.LM.LM36.reddito_netto",
  lossArtisans: "quadri.LM.LM37.perdite_artigiani_commercianti", lossSeparate: "quadri.LM.LM37.perdite_gestione_separata",
  loss80: "quadri.LM.LM37.perdite_misura_limitata_80", lossFull: "quadri.LM.LM37.perdite_misura_piena",
  lossTotal: "quadri.LM.LM37.perdite_totali_utilizzate", lm38: "quadri.LM.LM38.reddito_soggetto_imposta_sostitutiva",
  lm39: "quadri.LM.LM39.imposta_sostitutiva", lm40: "quadri.LM.LM40.totale_crediti_colonna_41", lm41: "quadri.LM.LM41.ritenute_consorzio",
  lm42: "quadri.LM.LM42.differenza", lm43: "quadri.LM.LM43.eccedenza_precedente",
  lm44: "quadri.LM.LM44.eccedenza_compensata_f24", lm45_1: "quadri.LM.LM45.acconti_sospesi",
  lm45_2: "quadri.LM.LM45.acconti_totali", lm46: "quadri.LM.LM46.imposta_debito",
  lm47: "quadri.LM.LM47.imposta_credito", lm49: "quadri.LM.LM49.eccedenza_contributi",
};

const RS_PATHS = {
  rs375: "quadri.RS.RS375001", rs376: "quadri.RS.RS376001", rs377: "quadri.RS.RS377001",
  rs378: "quadri.RS.RS378001", rs381: "quadri.RS.RS381001", rs382: "quadri.RS.RS382000",
};
const SPECIAL_VALUES = { deceased: "6", represented: "7", minor: "8" };
const BOOLEAN_SELECTS = new Set([
  "residencyEligible", "usesSpecialVatOrIncomeRegime", "primarilyRealEstateLandOrNewVehicles",
  "hasDisallowedParticipation", "mainlyWorksForCurrentOrRecentEmployer", "employmentRelationshipEnded",
  "newSimilarPrevious", "newContinuation", "newPracticeException", "newTakenOver",
]);
const INVERTED_BOOLEAN_SELECTS = new Set(["newSimilarPrevious", "newContinuation"]);
const getPath = (obj, path) => path.split(".").reduce((v, key) => v == null ? undefined : v[key], obj);
const controlValue = (control) => control.type === "checkbox" ? control.checked : control.type === "radio" ? (control.checked ? control.value : undefined) : control.value;
const controlPath = (control, paths) => paths[control.id]
  || (control.name === "sex" ? "frontespizio.sesso" : null)
  || (control.name === "representative-sex" ? "frontespizio.rappresentante_sesso" : null)
  || (control.name === "qualification" ? "quadri.LM.tipologia_attivita" : null)
  || activityControlPath(control)
  || creditControlPath(control)
  || (control.dataset?.lmField ? `quadri.LM.${control.dataset.lmField}` : null)
  || (control.dataset?.rsField ? `quadri.RS.${control.dataset.rsField}` : null);

const activityControlPath = (control) => {
  const row = control.closest?.("#activity-rows tr[data-row]");
  const field = control.dataset?.field;
  if (!row || !field) return null;
  const index = Number(row.dataset.row) - 22;
  const keys = { ateco: "codice_attivita", coefficient: "coefficiente_redditivita", positive: "componenti_positivi", copyright: "diritti_autore", income: "reddito_attivita", type: "tipologia_attivita", special: "caso_particolare" };
  return keys[field] ? `quadri.LM.attivita.${index}.${keys[field]}` : null;
};

const creditControlPath = (control) => {
  const code = control.dataset?.creditAmount || control.dataset?.creditAvailable;
  if (!code) return null;
  const columns = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,20,21,22,23,24,25,40];
  const column = Number(code.slice(-3)); const index = columns.indexOf(column);
  if (index < 0) return null;
  return control.dataset.creditAmount ? `quadri.LM.LM40.crediti.${index}.importo` : `quadri.LM.LM40.capienze.${code}`;
};

function useEngineAssets() {
  useEffect(() => {
    const links = STYLES.map((file) => {
      const id = `pf2026-engine-${file}`; let link = document.getElementById(id);
      if (!link) { link = document.createElement("link"); link.id = id; link.rel = "stylesheet"; link.href = `/pf2026-engine/${file}`; document.head.appendChild(link); }
      return link;
    });
    return () => links.forEach(() => {});
  }, []);
}

function hydrateControls(root, form, editing, paths) {
  root.querySelectorAll("input,select,textarea,output").forEach((control) => {
    const path = controlPath(control, paths);
    if (!path) return;
    const value = getPath(form, path);
    if (SPECIAL_VALUES[control.id]) control.checked = value === SPECIAL_VALUES[control.id];
    else if (control.type === "checkbox") control.checked = Boolean(value);
    else if (control.type === "radio") control.checked = value === control.value;
    else if (BOOLEAN_SELECTS.has(control.id) && typeof value === "boolean") control.value = String(INVERTED_BOOLEAN_SELECTS.has(control.id) ? !value : value);
    else if (value !== undefined && value !== null) control.value = value;
    else if (["lm34_1_input", "lm34_2_input"].includes(control.id)) control.value = "";
    control.disabled = !editing || control.tagName === "OUTPUT" || control.hasAttribute("readonly");
    control.dataset.fiscoxPath = path;
  });
  const province = root.querySelector("#representative-residence-province");
  const municipality = root.querySelector("#representative-residence-municipality");
  const foreignState = root.querySelector("#representative-residence-state");
  if (province && municipality && foreignState) {
    const abroad = province.value === "EE";
    municipality.classList.toggle("hidden", abroad);
    foreignState.classList.toggle("hidden", !abroad);
  }
}

function persistProgrammaticValues(root, paths, updField) {
  // Original PF2026 handlers update dependent DOM controls directly (for
  // example foreign-state code and destination choices). Mirror those values
  // into the FiscoX draft after the original target listener has run.
  const dependentIds = new Set([
    "foreign-residence-code", "representative-birth-place", "representative-birth-state",
    "representative-residence-province", "representative-residence-cap",
    "representative-residence-municipality", "representative-residence-state",
    "procedure-end", "eight-recipient", "eight-state-purpose", "eight-no-choice",
    "five-purpose", "five-tax-code", "five-no-choice", "two-party", "two-no-choice", "all-no-choice",
  ]);
  root.querySelectorAll("input,select,textarea").forEach((control) => {
    if (!dependentIds.has(control.id)) return;
    const path = control.dataset?.fiscoxPath || controlPath(control, paths);
    if (!path || control.tagName === "OUTPUT") return;
    const value = controlValue(control);
    if (value !== undefined) updField(path, value);
  });
}

function persistLmCalculatedValues(root, paths, updField) {
  ["lm34_3", "lm35_2", "lm36", "lm38", "lm39", "lm42", "lm46", "lm47", "lm49"].forEach((id) => {
    const control = root.querySelector(`#${id}`);
    const path = control && (control.dataset?.fiscoxPath || paths[id]);
    if (path && control.value !== undefined) updField(path, control.value);
  });
  root.querySelectorAll("#activity-rows tr[data-row]").forEach((row) => {
    ["coefficient", "income"].forEach((field) => {
      const control = row.querySelector(`[data-field="${field}"]`);
      const path = control && (control.dataset?.fiscoxPath || controlPath(control, paths));
      if (path) updField(path, control.value || "");
    });
  });
}

function renderFrontValidation(root, errors) {
  root.querySelectorAll(".field-error").forEach((node) => node.remove());
  root.querySelectorAll(".invalid").forEach((node) => node.classList.remove("invalid"));
  root.querySelector("#front-validation")?.remove();
  if (!errors?.length) return;
  errors.forEach((error) => {
    const control = error.controlId === "sex"
      ? root.querySelector('.sex-choice')
      : root.querySelector(`#${error.controlId}`);
    if (!control) return;
    const wrapper = control.closest?.(".software-input,.sex-choice,label") || control;
    wrapper.classList.add("invalid");
    const message = document.createElement("span");
    message.className = "field-error";
    message.textContent = error.message;
    wrapper.appendChild(message);
  });
  const summary = document.createElement("div");
  summary.id = "front-validation";
  summary.className = "validation-summary";
  summary.setAttribute("role", "alert");
  summary.setAttribute("aria-live", "polite");
  const title = document.createElement("b");
  title.textContent = "Completa i dati obbligatori prima di proseguire:";
  const list = document.createElement("ul");
  errors.forEach((error) => { const item = document.createElement("li"); item.textContent = error.message; list.appendChild(item); });
  summary.append(title, list);
  const taxpayer = root.querySelector(".taxpayer-data");
  taxpayer?.insertAdjacentElement("afterend", summary);
}

function renderLmRsValidation(root, errors, view) {
  root.querySelectorAll(".lm-rs-field-error").forEach((node) => node.remove());
  root.querySelectorAll(".lm-rs-invalid").forEach((node) => node.classList.remove("lm-rs-invalid"));
  root.querySelector("#lm-rs-validation")?.remove();
  const relevant = (errors || []).filter((error) => view === "rs" ? error.targetView === "RS" : true);
  if (!relevant.length) return;
  relevant.forEach((error) => {
    const controls = error.selector ? root.querySelectorAll(error.selector) : [root.querySelector(`#${error.controlId}`)].filter(Boolean);
    controls.forEach((control, index) => {
      const wrapper = control.closest?.("label,fieldset,td") || control; wrapper.classList.add("lm-rs-invalid");
      if (index === 0) { const message = document.createElement("span"); message.className = "field-error lm-rs-field-error"; message.textContent = error.message; wrapper.appendChild(message); }
    });
  });
  const summary = document.createElement("div"); summary.id = "lm-rs-validation"; summary.className = "validation-summary"; summary.setAttribute("role", "alert");
  summary.innerHTML = `<b>Completa i dati obbligatori prima dell’invio:</b><ul>${relevant.map((error) => `<li>${String(error.message).replace(/&/g,"&amp;").replace(/</g,"&lt;")}</li>`).join("")}</ul>`;
  root.querySelector(".page-title")?.insertAdjacentElement("afterend", summary);
}

function addRequiredMarker(control) {
  if (!control) return;
  control.setAttribute("aria-required", "true");
  const wrapper = control.closest?.("label,fieldset,td") || control;
  const caption = wrapper.querySelector?.(wrapper.tagName === "FIELDSET" ? ":scope > legend" : ":scope > span")
    || wrapper.querySelector?.("legend,span,b");
  if (!caption || caption.querySelector?.(".required-marker")) return;
  const marker = document.createElement("strong"); marker.className = "required-marker"; marker.setAttribute("aria-hidden", "true"); marker.textContent = " *";
  caption.appendChild(marker);
}

function markLmRequiredFields(root) {
  root.querySelectorAll('input[name="qualification"]').forEach(addRequiredMarker);
  ["ateco2025", "requirements", "noExclusions"].forEach((id) => addRequiredMarker(root.querySelector(`#${id}`)));
  const firstActivity = root.querySelector('#activity-rows tr[data-row="22"]');
  addRequiredMarker(firstActivity?.querySelector('[data-field="ateco"]'));
  addRequiredMarker(firstActivity?.querySelector('[data-field="positive"]'));
}

function markFrontRequiredFields(root) {
  FRONT_REQUIRED_CONTROL_IDS.forEach((controlId) => {
    const control = controlId === "sex" ? root.querySelector(".sex-choice") : root.querySelector(`#${controlId}`);
    if (!control) return;
    control.setAttribute("aria-required", "true");
    const wrapper = control.closest?.("label,fieldset") || control;
    const caption = wrapper.querySelector?.(wrapper.tagName === "FIELDSET" ? ":scope > legend" : ":scope > span")
      || wrapper.querySelector?.("legend,span");
    if (!caption || caption.querySelector(".required-marker")) return;
    const marker = document.createElement("strong");
    marker.className = "required-marker";
    marker.setAttribute("aria-hidden", "true");
    marker.textContent = " *";
    caption.appendChild(marker);
  });
}

function EngineView({ html, form, editing, updField, paths, view, validationErrors, telematicProfile }) {
  const host = useRef(null);
  const latest = useRef({ form, editing, paths });
  latest.current = { form, editing, paths };
  const [municipalities, setMunicipalities] = useState([]);
  const frontMunicipalities = view === "front" ? municipalities : null;
  useEffect(() => { if (host.current) host.current.innerHTML = html; }, [html]);
  useEffect(() => {
    if (view !== "front") return;
    fetch("/pf2026-engine/municipalities.json").then((r) => r.json()).then(setMunicipalities).catch(() => setMunicipalities([]));
  }, [view]);
  useEffect(() => {
    let cancelled = false;
    const installOriginalUi = async () => {
      if (!host.current) return;
      if (view === "front") {
        const [module, foreignStates] = await Promise.all([
          import(/* webpackIgnore: true */ `/pf2026-engine/frontespizio-extra.js?v=${FRONTESPIZIO_ASSET_VERSION}`),
          fetch("/pf2026-engine/foreign-states-rpf2026.json").then((response) => response.json()),
        ]);
        if (!cancelled) {
          module.installFrontespizioExtras(frontMunicipalities, foreignStates, telematicProfile);
          const options = host.current?.querySelector("#municipality-options");
          if (options) {
            options.replaceChildren(...frontMunicipalities.map((item) => {
              const option = document.createElement("option"); option.value = item.name;
              option.textContent = `${item.province} · ${item.code}`; return option;
            }));
          }
        }
      } else if (view === "lm") {
        const [rowsModule, module, creditsModule, calculationsModule] = await Promise.all([
          import(/* webpackIgnore: true */ `/pf2026-engine/lm-activity-rows.js?v=${LM_ASSET_VERSION}`),
          import(/* webpackIgnore: true */ `/pf2026-engine/lm-full.js?v=${LM_ASSET_VERSION}`),
          import(/* webpackIgnore: true */ `/pf2026-engine/lm-credit-lines.js?v=${LM_ASSET_VERSION}`),
          import(/* webpackIgnore: true */ `/pf2026-engine/lm-live-calculations.js?v=${LM_ASSET_VERSION}`),
        ]);
        if (!cancelled) {
          rowsModule.renderActivityRows();
          await module.installFullLm();
          creditsModule.bindLmCreditLines();
          await calculationsModule.installLmLiveCalculations(host.current?.querySelector("#lm-view"));
        }
      } else if (view === "rs") {
        const module = await import(/* webpackIgnore: true */ "/pf2026-engine/rs-full.js");
        if (!cancelled) await module.installFullRs();
      }
      if (!cancelled && host.current) {
        const originalView = host.current.querySelector(".model-view");
        originalView?.classList.remove("hidden");
        originalView?.classList.add("active");
        hydrateControls(host.current, latest.current.form, latest.current.editing, latest.current.paths);
        if (view === "front") markFrontRequiredFields(host.current);
        if (view === "lm") { markLmRequiredFields(host.current); originalView?.dispatchEvent(new Event("input", { bubbles: true })); }
      }
    };
    if (view !== "front" || frontMunicipalities?.length) installOriginalUi().catch((error) => {
      if (!cancelled) console.error(`PF2026 Engine ${view}:`, error);
    });
    return () => { cancelled = true; };
  }, [frontMunicipalities, html, telematicProfile, view]);
  useEffect(() => {
    const root = host.current; if (!root) return;
    hydrateControls(root, form, editing, paths);
    if (view === "lm") { markLmRequiredFields(root); root.querySelector("#lm-view")?.dispatchEvent(new Event("input", { bubbles: true })); }
    if (view === "front") {
      markFrontRequiredFields(root);
      renderFrontValidation(root, validationErrors);
    }
    if (view === "lm" || view === "rs") renderLmRsValidation(root, validationErrors, view);
  }, [editing, form, html, paths, validationErrors, view]);
  useEffect(() => {
    const root = host.current; if (!root) return undefined;
    const onChange = (event) => {
      const control = event.target; const path = control.dataset?.fiscoxPath || controlPath(control, paths); if (!path || !editing) return;
      control.dataset.fiscoxPath = path;
      let value = SPECIAL_VALUES[control.id]
        ? (control.checked ? SPECIAL_VALUES[control.id] : (getPath(form, path) === SPECIAL_VALUES[control.id] ? "" : getPath(form, path)))
        : controlValue(control);
      if (BOOLEAN_SELECTS.has(control.id)) {
        value = value === "" ? null : value === "true";
        if (INVERTED_BOOLEAN_SELECTS.has(control.id) && value !== null) value = !value;
      }
      if (value !== undefined) updField(path, value);
      if (control.dataset?.creditAmount) {
        const columns = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,20,21,22,23,24,25,40];
        const column = Number(control.dataset.creditAmount.slice(-3));
        const index = columns.indexOf(column);
        if (index >= 0) updField(`quadri.LM.LM40.crediti.${index}.colonna`, column);
      }
      if (control.id === "fiscalCode" && String(value).length === 16) {
        const decoded = decodeItalianFiscalCode(value, municipalities);
        if (decoded) {
          updField("frontespizio.data_nascita", decoded.birthDate); updField("frontespizio.sesso", decoded.sex);
          if (decoded.birthPlace) updField("frontespizio.comune_nascita", decoded.birthPlace);
          if (decoded.birthProvince) updField("frontespizio.provincia_nascita", decoded.birthProvince);
        }
      }
      if (["residence-municipality", "domicile-2025-municipality", "domicile-2026-municipality"].includes(control.id)) {
        const found = municipalities.find((item) => item.name.localeCompare(value, "it", { sensitivity: "base" }) === 0);
        const prefix = control.id.replace("-municipality", "");
        if (found) {
          const province = root.querySelector(`#${prefix}-province`), cap = root.querySelector(`#${prefix}-postcode`), cadastral = root.querySelector(`#${prefix}-cadastral-code`);
          if (province) { province.value = found.province || ""; updField(paths[province.id], province.value); }
          if (cap) { cap.value = found.caps?.length === 1 ? found.caps[0] : ""; updField(paths[cap.id], cap.value); }
          if (cadastral) { cadastral.value = found.code || ""; updField(paths[cadastral.id], cadastral.value); }
        }
      }
      if (control.id === "representative-residence-province") {
        updField("frontespizio.rappresentante_comune_residenza", "");
        updField("frontespizio.rappresentante_cap", "");
        updField("frontespizio.rappresentante_residente_estero", value === "EE");
      }
      // Run after all original PF2026 listeners attached to the target have
      // completed, so their dependent values are persisted as well.
      queueMicrotask(() => persistProgrammaticValues(root, paths, updField));
      if (view === "lm") queueMicrotask(() => persistLmCalculatedValues(root, paths, updField));
    };
    root.addEventListener("input", onChange); root.addEventListener("change", onChange);
    return () => { root.removeEventListener("input", onChange); root.removeEventListener("change", onChange); };
  }, [editing, form, municipalities, paths, updField, view]);
  return <div ref={host} className="pf2026-engine-embedded" data-testid={`rpf26-original-${view}`} />;
}

export function Rpf26EngineWorkspace({ active, form, editing, updField, validationErrors = [], telematicProfileOverride = null }) {
  useEngineAssets(); const [views, setViews] = useState(null); const [error, setError] = useState("");
  const [loadedTelematicProfile, setLoadedTelematicProfile] = useState({ isIntermediary: false, intermediaryTaxCode: "" });
  const telematicProfile = telematicProfileOverride || loadedTelematicProfile;
  useEffect(() => { fetch(`/pf2026-engine/views.json?v=${LM_ASSET_VERSION}`).then((r) => { if (!r.ok) throw new Error("UI PF2026 Engine non disponibile"); return r.json(); }).then(setViews).catch((e) => setError(e.message)); }, []);
  useEffect(() => { fetch("/api/telematico/settings", { credentials: "include" }).then((r) => r.ok ? r.json() : null).then((settings) => {
    if (!settings) return;
    setLoadedTelematicProfile({ isIntermediary: settings.invio_conto_proprio === false, intermediaryTaxCode: settings.codice_fiscale_intermediario || "" });
  }).catch(() => {}); }, []);
  useEffect(() => {
    if (!editing || !telematicProfile.isIntermediary || !telematicProfile.intermediaryTaxCode) return;
    const trustedTaxCode = telematicProfile.intermediaryTaxCode.trim().toUpperCase();
    if (form?.frontespizio?.impegno_codice_fiscale_incaricato !== trustedTaxCode) {
      updField("frontespizio.impegno_codice_fiscale_incaricato", trustedTaxCode);
    }
  }, [editing, form?.frontespizio?.impegno_codice_fiscale_incaricato, telematicProfile, updField]);
  const config = useMemo(() => active === "FRONT" ? ["front", FRONT_PATHS] : active === "LM" ? ["lm", LM_PATHS] : ["rs", RS_PATHS], [active]);
  if (error) return <div className="m-4 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-800">{error}</div>;
  if (!views) return <div className="p-6 text-sm text-slate-500">Caricamento interfaccia originale PF2026 Engine…</div>;
  return <EngineView key={`${config[0]}-${telematicProfile.isIntermediary}`} html={views[config[0]]} form={form} editing={editing} updField={updField} paths={config[1]} view={config[0]} validationErrors={validationErrors} telematicProfile={telematicProfile} />;
}
